import React, { useState, useEffect } from 'react';
import { Event, getCalendarMonth, getEvents } from '../api';

export function CalendarPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [calendarData, setCalendarData] = useState<Map<string, number>>(new Map());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<Event[]>([]);

  const loadCalendarData = async () => {
    try {
      const data = await getCalendarMonth(year, month);
      const map = new Map<string, number>();
      data.forEach((d) => map.set(d.date, d.event_count));
      setCalendarData(map);
    } catch (err) {
      console.error('Error loading calendar:', err);
    }
  };

  useEffect(() => {
    loadCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);


  async function selectDate(date: string) {
    setSelectedDate(date);
    try {
      const events = await getEvents(date);
      setSelectedEvents(events || []);
    } catch (err) {
      console.error('Error loading events:', err);
      setSelectedEvents([]);
    }
  }

  function previousMonth() {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
    setSelectedDate(null);
  }

  function nextMonth() {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
    setSelectedDate(null);
  }

  const monthName = new Date(year, month - 1).toLocaleString('en-US', { month: 'long' });
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const weeks = [];
  let week = Array(firstDay).fill(null);

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const eventCount = calendarData.get(dateStr) || 0;
    week.push({ day, dateStr, eventCount });

    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }

  if (week.length > 0) {
    while (week.length < 7) {
      week.push(null);
    }
    weeks.push(week);
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h2 className="text-4xl font-bold text-gray-800 mb-8">Event Calendar</h2>

      <div className="grid grid-cols-3 gap-8">
        {/* Calendar */}
        <div className="col-span-2">
          <div className="bg-white rounded-lg shadow-lg p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <button
                onClick={previousMonth}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition"
              >
                ← Prev
              </button>
              <h3 className="text-2xl font-bold">
                {monthName} {year}
              </h3>
              <button
                onClick={nextMonth}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition"
              >
                Next →
              </button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-2 mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center font-bold text-gray-600 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-2">
              {weeks.map((week, weekIdx) =>
                week.map((day, dayIdx) => (
                  <button
                    key={`${weekIdx}-${dayIdx}`}
                    onClick={() => day && selectDate(day.dateStr)}
                    className={`aspect-square p-2 rounded flex flex-col items-center justify-center text-sm transition ${
                      !day
                        ? 'bg-gray-50'
                        : selectedDate === day.dateStr
                        ? 'bg-pink-500 text-white font-bold'
                        : day.eventCount > 0
                        ? 'bg-pink-100 hover:bg-pink-200 text-gray-800 font-semibold'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                    disabled={!day}
                  >
                    {day && (
                      <>
                        <span className="text-lg">{day.day}</span>
                        {day.eventCount > 0 && (
                          <span className="text-xs bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center mt-1">
                            {day.eventCount}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Selected Date Events */}
        <div>
          {selectedDate ? (
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-20">
              <h3 className="text-xl font-bold mb-4 text-gray-800">
                {new Date(selectedDate).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </h3>

              {selectedEvents.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {selectedEvents.map((event) => (
                    <div
                      key={event.id}
                      className="border-l-4 border-pink-500 bg-gray-50 p-3 rounded"
                    >
                      <p className="font-bold text-sm text-gray-800">{event.title}</p>
                      <p className="text-xs text-blue-600 mt-1">
                        {new Date(event.start_time).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          timeZone: 'America/New_York',
                        })} EST
                      </p>
                      {event.venue_name && (
                        <p className="text-xs text-gray-600 mt-1">📍 {event.venue_name}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-sm">No events scheduled</p>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-6 text-center text-gray-600">
              <p>Select a date to view events</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
